import React, { useEffect, useState } from 'react';
import { Card, Row, Col, Statistic, Divider } from 'antd';
import {
  UserOutlined,
  ThunderboltOutlined,
  BankOutlined,
  DollarOutlined,
  ShopOutlined,
  HomeOutlined,
  ApiOutlined,
  FireOutlined,
  LineChartOutlined,
} from '@ant-design/icons';
import ChargingHttpHandler from '../../shared/chargingHttpHandler';
import secureStorage from '../../shared/secureStorage';
import dayjs from 'dayjs';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';

// 設置 dayjs 插件
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

const HomeTab = () => {
  const searchTotalMember = async () => {
    const httpHandler = new ChargingHttpHandler(false);
    const chargingAccessToken = secureStorage.getItem('chargingAccessToken');
    // console.log('chargingAccessToken', chargingAccessToken);
    const userRole = secureStorage.getItem('userRole');
    console.log('API完整路徑:', httpHandler.baseURL + '/platform/get_member_info');
    const response = await httpHandler
      .axios()
      .get('/platform/get_member_info', {
        headers: {
          Authorization: `Bearer ${chargingAccessToken}`,
        },
      });
    const members = Array.isArray(response.data?.data)
      ? response.data.data
      : [];
    const TotalMember = members.length;
    // console.log('TotalMember', TotalMember);
    secureStorage.setItem('TotalMember', TotalMember);

    // 用戶基本統計 - 當月有效、累計、當月新增、當月取消開通
    const now = dayjs();
    const isSameMonth = (ts) => (ts ? dayjs(ts).isSame(now, 'month') : false);

    const monthlyActive = members.filter((m) => m?.is_deleted === 0).length; // 有效用戶數（is_deleted=0）
    const cumulative = members.length; // 累計用戶數
    const monthlyNew = members.filter((m) => isSameMonth(m?.created_at)).length; // 當月新增用戶（created_at）
    const monthlyCanceled = members.filter((m) => isSameMonth(m?.closed_at)).length; // 當月取消開通（closed_at）

    setMonthlyActiveUsers(monthlyActive);
    setCumulativeUsers(cumulative);
    setMonthlyNewUsers(monthlyNew);
    setMonthlyCanceledUsers(monthlyCanceled);

    // 記錄所有 account 用於交叉比對
    const accounts = members.map((m) => m?.account).filter(Boolean);
    setMemberAccounts(accounts);
  };

  useEffect(() => {
    searchTotalMember();
  }, []);

  const TotalMember = secureStorage.getItem('TotalMember');

  // 新增：充電次數、總充電量、總金額
  const [orderCount, setOrderCount] = useState(0);
  const [totalKwh, setTotalKwh] = useState(0);
  const [totalAmount, setTotalAmount] = useState(0);

  // 新增：合作廠商與硬體資料統計
  const [operatorCount, setOperatorCount] = useState(0);
  const [chargingStationCount, setChargingStationCount] = useState(0);
  const [chargingGunCount, setChargingGunCount] = useState(0);

  // 新增：交易資料統計
  const [monthlyKwh, setMonthlyKwh] = useState(0);
  const [monthlyAvgPrice, setMonthlyAvgPrice] = useState(0);

  // 新增：用戶基本統計
  const [monthlyActiveUsers, setMonthlyActiveUsers] = useState(0);
  const [cumulativeUsers, setCumulativeUsers] = useState(0);
  const [monthlyNewUsers, setMonthlyNewUsers] = useState(0);
  const [monthlyCanceledUsers, setMonthlyCanceledUsers] = useState(0);
  const [memberAccounts, setMemberAccounts] = useState([]);
  const [monthlyOrderUserIds, setMonthlyOrderUserIds] = useState([]);

  const add_search_log = async (success) => {
    const userRole = secureStorage.getItem('userRole');
    const httpHandler = new ChargingHttpHandler(false);
    const chargingAccessToken = secureStorage.getItem('chargingAccessToken');
    // 紀錄搜尋時間
    console.log('API完整路徑:', httpHandler.baseURL + '/platform/update_user_log_table');
    await httpHandler
      .axios()
      .post(
        '/platform/update_user_log_table',
        {
          log_time: dayjs().format('YYYY/MM/DD HH:mm:ss'),
          user_role: userRole,
          action_type: '訂單搜尋',
          action_content: '用戶搜尋訂單資訊',
          status: success,
        },
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${chargingAccessToken}`,
          },
        }
      )
      .catch((e) => {
        console.error('寫入搜尋紀錄失敗', e);
      });
  };

  // 當會員帳號或本月訂單 userId 更新時，重算當月有效人數
  useEffect(() => {
    if (!memberAccounts || memberAccounts.length === 0) {
      setMonthlyActiveUsers(0);
      return;
    }
    const accountSet = new Set(memberAccounts);
    const activeCount = monthlyOrderUserIds.filter((uid) =>
      accountSet.has(uid)
    ).length;
    setMonthlyActiveUsers(activeCount);
  }, [memberAccounts, monthlyOrderUserIds]);

  useEffect(() => {
    const fetchOrderStats = async () => {
      const httpHandler = new ChargingHttpHandler(false);
      const chargingAccessToken = secureStorage.getItem('chargingAccessToken');
      try {
        console.log('API完整路徑:', httpHandler.baseURL + '/order/all-cdr-info');
        const response = await httpHandler.axios().get('/order/all-cdr-info', {
          headers: { Authorization: `Bearer ${chargingAccessToken}` },
        });
        if (response.data && Array.isArray(response.data.data)) {
          await add_search_log(1);
          const orders = response.data.data;
          setOrderCount(orders.length);
          setTotalKwh(
            orders.reduce(
              (sum, item) => sum + (parseFloat(item.chargedKwh) || 0),
              0
            )
          );
          setTotalAmount(
            orders.reduce(
              (sum, item) => sum + (parseFloat(item.totalAmount) || 0),
              0
            )
          );

          // 計算當月統計
          const currentMonth = dayjs().format('YYYY-MM');
          let monthlyKwhTotal = 0;
          let monthlyAmountTotal = 0;

          orders.forEach((order) => {
            const chargeStartTime = dayjs(order.chargeStartTime);
            const chargeEndTime = dayjs(order.chargeEndTime);

            // 檢查充電時間是否涵蓋到本月
            const startMonth = chargeStartTime.format('YYYY-MM');
            const endMonth = chargeEndTime.format('YYYY-MM');

            if (startMonth === currentMonth || endMonth === currentMonth) {
              const kwh = parseFloat(order.chargedKwh) || 0;
              const amount = parseFloat(order.totalAmount) || 0;

              monthlyKwhTotal += kwh;
              monthlyAmountTotal += amount;
            }
          });

          setMonthlyKwh(monthlyKwhTotal);
          setMonthlyAvgPrice(
            monthlyKwhTotal > 0 ? monthlyAmountTotal / monthlyKwhTotal : 0
          );

          console.log('當月充電度數:', monthlyKwhTotal);
          console.log(
            '當月平均交易電價:',
            monthlyKwhTotal > 0 ? monthlyAmountTotal / monthlyKwhTotal : 0
          );

          // 蒐集本月訂單的 userId
          const currentMonthUserIds = orders
            .filter((o) => dayjs(o.chargeEndTime).isSame(dayjs(), 'month'))
            .map((o) => o.userId)
            .filter(Boolean);
          const uniqueUserIds = [...new Set(currentMonthUserIds)];
          setMonthlyOrderUserIds(uniqueUserIds);
        } else {
          await add_search_log(0);
        }
      } catch (e) {
        console.error('取得訂單統計資料失敗', e);
      }
    };
    fetchOrderStats();
  }, []);

  // 新增：獲取營運商統計資料
  useEffect(() => {
    const fetchOperatorStats = async () => {
      console.log('開始獲取營運商統計資料...');
      const httpHandler = new ChargingHttpHandler(false);
      const chargingAccessToken = secureStorage.getItem('chargingAccessToken');
      console.log('chargingAccessToken:', chargingAccessToken);

      try {
        console.log('API完整路徑:', httpHandler.baseURL + '/options/business');
        console.log('發送 API 請求到 /business');
        const response = await httpHandler.axios().get('options/business', {
          headers: {
            Authorization: `Bearer ${chargingAccessToken}`,
            'x-api-key':
              'N+NBld9Zj/jsW0SCmjA1k1m/PwatZQ+aQdxURx/l98UcZ//7RxRH59eNgMgAys7t',
          },
        });
        console.log('Business API 回應:', response);

        if (response.data && Array.isArray(response.data.data)) {
          const businesses = response.data.data;
          console.log('營運商資料:', businesses);

          // 計算 data 底下的 id 數量
          const businessIds = businesses
            .map((business) => business.id)
            .filter((id) => id);
          setOperatorCount(businessIds.length);
          console.log('營運商數量:', businessIds.length);
        } else {
          console.log('Business API 回應格式不正確:', response.data);
        }
      } catch (e) {
        console.error('取得營運商統計資料失敗', e);
      }
    };
    fetchOperatorStats();
  }, []);

  useEffect(() => {
    const fetchLocationStats = async () => {
      const httpHandler = new ChargingHttpHandler(false);

      try {
        console.log('API完整路徑:', httpHandler.baseURL + '/location/get-all-location');
        console.log('發送 API 請求到 /location/get-all-location');
        const response = await httpHandler
          .axios()
          .get('/location/get-all-location', {
            // 使用後台的key 要進行AES加密 這邊直接寫死
            headers: {
              'api-key':
                'HSQ8U2exG2rBvUnpStdP36od/8fR3d33P9viJHCpURAupN1HYUHu57kKul2N4vxa',
            },
          });
        // console.log('Location API 回應:', response);

        if (
          response.data &&
          response.data.data &&
          Array.isArray(response.data.data.locations)
        ) {
          const locations = response.data.data.locations;
          // console.log('充電站資料:', locations);

          // 統計充電站數量（locationId 的數量）
          const locationIds = locations
            .map((location) => location.locationId)
            .filter((id) => id);
          const uniqueLocationIds = [...new Set(locationIds)];
          setChargingStationCount(uniqueLocationIds.length);
          // console.log('充電站數量:', uniqueLocationIds.length);

          // 統計充電槍數量（acTotal + dcTotal）
          const totalGuns = locations.reduce((sum, location) => {
            const acTotal = parseInt(location.acTotal) || 0;
            const dcTotal = parseInt(location.dcTotal) || 0;
            return sum + acTotal + dcTotal;
          }, 0);
          setChargingGunCount(totalGuns);
          // console.log('充電槍總數:', totalGuns);
        } else {
          // console.log('Location API 回應格式不正確:', response.data);
        }
      } catch (e) {
        console.error('取得充電站和充電槍統計資料失敗', e);
      }
    };
    fetchLocationStats();
  }, []);

  return (
    <div>
      {/* 基本統計資料 */}
      <Divider
        orientation="left"
        style={{ fontSize: '16px', fontWeight: 'bold' }}>
        用戶基本統計
      </Divider>
      <Row gutter={[24, 24]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title={
                <span>
                  <UserOutlined style={{ marginRight: 8 }} />
                  當月有效人數
                </span>
              }
              value={monthlyActiveUsers}
              suffix="人"
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title={
                <span>
                  <UserOutlined style={{ marginRight: 8 }} />
                  當月累計用戶數
                </span>
              }
              value={cumulativeUsers}
              suffix="人"
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title={
                <span>
                  <UserOutlined style={{ marginRight: 8 }} />
                  當月新增用戶數
                </span>
              }
              value={monthlyNewUsers}
              suffix="人"
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title={
                <span>
                  <UserOutlined style={{ marginRight: 8 }} />
                  當月取消開通用戶數
                </span>
              }
              value={monthlyCanceledUsers}
              suffix="人"
            />
          </Card>
        </Col>
      </Row>

      {/* 合作廠商與硬體資料統計 */}
      <Divider
        orientation="left"
        style={{ fontSize: '16px', fontWeight: 'bold' }}>
        合作廠商與硬體資料統計
      </Divider>
      <Row gutter={[24, 24]}>
        <Col xs={24} sm={12} lg={8}>
          <Card>
            <Statistic
              title={
                <span>
                  <ShopOutlined style={{ marginRight: 8 }} />
                  營運商
                </span>
              }
              value={operatorCount}
              suffix="個"
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={8}>
          <Card>
            <Statistic
              title={
                <span>
                  <HomeOutlined style={{ marginRight: 8 }} />
                  充電站
                </span>
              }
              value={chargingStationCount}
              suffix="站"
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={8}>
          <Card>
            <Statistic
              title={
                <span>
                  <ApiOutlined style={{ marginRight: 8 }} />
                  充電槍
                </span>
              }
              value={chargingGunCount}
              suffix="個"
            />
          </Card>
        </Col>
      </Row>

      {/* 交易資料 */}
      <Divider
        orientation="left"
        style={{ fontSize: '16px', fontWeight: 'bold', marginTop: '32px' }}>
        交易資料
      </Divider>
      <Row gutter={[24, 24]}>
        <Col xs={24} sm={12} lg={12}>
          <Card>
            <Statistic
              title={
                <span>
                  <FireOutlined style={{ marginRight: 8 }} />
                  當月充電度數
                </span>
              }
              value={monthlyKwh.toFixed(2)}
              suffix="kWh"
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={12}>
          <Card>
            <Statistic
              title={
                <span>
                  <LineChartOutlined style={{ marginRight: 8 }} />
                  當月平均交易電價
                </span>
              }
              value={monthlyAvgPrice.toFixed(2)}
              suffix="元/kWh"
            />
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default HomeTab;
